/**
 * As colunas do papel de trabalho, na ORDEM da planilha que o contador já usa
 * ("856 - Composição Conta 177 Fornecedores", aba "Composição do Saldo").
 *
 * A ordem NÃO é estética: o contador confere de cima para baixo, coluna a
 * coluna, contra o arquivo dele. Trocar duas de lugar transforma a conferência
 * numa caça ao erro.
 *
 * Cinco colunas (nota fiscal, série, data da nota, fornecedor, CNPJ) não
 * existem em `extract_lancamentos` — vêm do casamento com
 * `extract_notas_entrada` feito no backend (`enriquecimento_nota`), que fecha
 * em 63% dos itens. As outras 37% mostram "—", e isso é intencional:
 * pagamento (PIX, boleto, transferência) não tem nota fiscal, e "não achei a
 * nota" precisa ser distinguível de "a nota diz X".
 *
 * Este módulo existe separado do `index.tsx` porque o alinhamento do rodapé
 * depende dele aritmeticamente (ver `COLSPAN_TOTAL_ROTULO`) e isso precisa
 * estar sob teste: uma coluna nova desalinharia o TOTAL sem erro de tipo nem
 * de lint — a tabela simplesmente passaria a somar debaixo da coluna errada.
 */

export interface IColunaPapel {
  titulo: string;
  alinha?: 'left' | 'right';
  /**
   * Largura da coluna. NÃO é ajuste fino de estética: sem ela o `table-layout`
   * automático reparte o espaço pelo conteúdo, e as colunas curtas (Filial,
   * Dias, CNPJ — todas de tamanho fixo) roubam largura das duas colunas de
   * texto que o contador realmente lê. Medido em produção com as 13 colunas:
   * "Fornecedor" e "Histórico" ficaram com ~75px e truncavam em uma palavra
   * ("GOOGLE…", "SERVIÇO…"), enquanto "Contrapartida" quebrava em três linhas.
   */
  largura: string;
}

/**
 * As larguras fixas saíram de MEDIÇÃO do conteúdo real em produção (empresa
 * 856, conta 177), não de estimativa: o texto mais largo de cada coluna, mais
 * o padding. Chutar foi o que produziu CNPJ colidindo com Filial na primeira
 * tentativa — `25.012.398/0001-07` precisa de 150px, não dos 134px que eu
 * tinha suposto.
 */
export const COLUNAS_COMPOSICAO: IColunaPapel[] = [
  { titulo: 'Data', largura: '99px' },
  { titulo: 'Lanç.', largura: '66px' },
  { titulo: 'Nota fiscal', largura: '92px' },
  { titulo: 'Série', largura: '58px' },
  { titulo: 'Data da nota', largura: '104px' },
  // As três colunas de texto. Em px, não em %: com `table-layout: fixed` a
  // porcentagem é calculada sobre a largura da TABELA, então "78% + 809px de
  // colunas fixas" dá 2027px numa tabela declarada com 1560px — ela estoura o
  // container em ~470px. Foi o teste de aritmética que pegou isso.
  //
  // Fornecedor precisaria de 463px e Contrapartida de 253px para NUNCA
  // truncar (medido no dado real); somados não cabem em tela nenhuma. Elas
  // truncam com reticências e o texto inteiro fica no `title` (hover), em vez
  // de espremer o resto da tabela.
  { titulo: 'Fornecedor', largura: '280px' },
  { titulo: 'CNPJ', largura: '150px' },
  { titulo: 'Filial', largura: '62px' },
  { titulo: 'Contrapartida', largura: '210px' },
  { titulo: 'Valor original', alinha: 'right', largura: '106px' },
  { titulo: 'Em aberto', alinha: 'right', largura: '112px' },
  { titulo: 'Dias', alinha: 'right', largura: '60px' },
  { titulo: 'Histórico', largura: '260px' },
];

/**
 * Colunas da aba RAZÃO.
 *
 * São as 10 da planilha de referência (aba "Razão da Conta 177"), na ordem
 * dela, mais a coluna de SALDO ACUMULADO no fim.
 *
 * O acumulado não existe na planilha — verificado nas 12.215 linhas: zero
 * fórmulas, zero totalizadores; o ERP exporta só o movimento. Ele entra aqui
 * porque um razão sem saldo corrente obriga o contador a somar de cabeça para
 * saber onde a conta estava em cada data, que é o trabalho que a tela deveria
 * poupar. Vai no FIM para não deslocar as colunas que ele já confere contra o
 * arquivo dele.
 *
 * "Tipo" é o nome que a planilha dá ao lado do lançamento (lá: PROVISAO /
 * PAGAMENTO). Aqui mostramos D/C, que é o que `extract_lancamentos` afirma —
 * chamar de provisão o que é só um crédito seria interpretar o dado.
 */
export const COLUNAS_RAZAO: IColunaPapel[] = [
  { titulo: 'Data', largura: '99px' },
  { titulo: 'Lanç.', largura: '66px' },
  { titulo: 'Tipo', largura: '58px' },
  { titulo: 'Valor', alinha: 'right', largura: '118px' },
  { titulo: 'Nota fiscal', largura: '92px' },
  { titulo: 'Fornecedor', largura: '260px' },
  { titulo: 'CNPJ', largura: '150px' },
  { titulo: 'Filial', largura: '62px' },
  { titulo: 'Contrapartida', largura: '200px' },
  { titulo: 'Histórico', largura: '260px' },
  { titulo: 'Saldo acumulado', alinha: 'right', largura: '132px' },
];

/** As colunas de texto livre — as que truncam e carregam `title` no hover. */
const COLUNAS_DE_TEXTO = ['Fornecedor', 'Contrapartida', 'Histórico'];

export const ehColunaDeTexto = (titulo: string): boolean => COLUNAS_DE_TEXTO.includes(titulo);

/**
 * Soma das larguras de um conjunto de colunas, em px.
 *
 * É sempre DERIVADO, nunca escrito à mão: um número independente das colunas
 * foi o que deixou "Fornecedor" com 119px ("GOOGLE…") na primeira subida da
 * composição — 1180px declarados contra ~1470px de colunas.
 */
export const larguraMinima = (colunas: IColunaPapel[]): string =>
  `${colunas.reduce((soma, c) => soma + parseFloat(c.largura), 0)}px`;

/** Usado nos `colSpan` das linhas que atravessam a tabela da razão. */
export const N_COLUNAS_RAZAO = COLUNAS_RAZAO.length;

export const LARGURA_MINIMA_RAZAO = larguraMinima(COLUNAS_RAZAO);

/** Índice (base 0) da coluna "Saldo acumulado" — a última da razão. */
export const IDX_SALDO_ACUMULADO = COLUNAS_RAZAO.findIndex(c => c.titulo === 'Saldo acumulado');

/** Índice (base 0) da coluna "Valor" — é onde o rodapé imprime D/C do período. */
export const IDX_VALOR_RAZAO = COLUNAS_RAZAO.findIndex(c => c.titulo === 'Valor');

/**
 * `colSpan` do rótulo "Movimento do período", para que a célula seguinte caia
 * EXATAMENTE sob "Valor".
 *
 * Derivado, nunca hardcoded. Números mágicos de colSpan já me custaram dois PRs
 * corretivos na composição: o rodapé desalinha sem erro de tipo, sem erro de
 * lint e sem quebrar o HTML — a tabela só passa a somar debaixo da coluna
 * errada, e ninguém percebe até o contador reclamar.
 */
export const COLSPAN_RAZAO_ROTULO = IDX_VALOR_RAZAO;

/** Colunas entre "Valor" e "Saldo acumulado", que o rodapé atravessa vazias. */
export const COLSPAN_RAZAO_MEIO = IDX_SALDO_ACUMULADO - IDX_VALOR_RAZAO - 1;

/**
 * A geometria das 13 colunas da composição é DERIVADA da escolha do usuário,
 * não fixa: ver `geometria()` em `colunasVisiveis.ts`.
 *
 * Havia aqui quatro constantes (`LARGURA_MINIMA_TABELA`, `N_COLUNAS`,
 * `IDX_EM_ABERTO`, `COLSPAN_TOTAL_ROTULO`/`_RESTO`) calculadas sobre as 13
 * colunas sempre visíveis. Com a escolha por usuário elas passaram a descrever
 * uma tabela que não existe mais em tela nenhuma — e o risco que elas
 * protegiam (o TOTAL somar debaixo da coluna errada) migrou inteiro para
 * `geometria()`, que é onde está testado.
 *
 * A razão NÃO tem escolha de colunas, então as constantes dela continuam
 * fixas logo acima.
 */
